"use client";

import { useLocale, useTranslations } from "next-intl";

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { formatDateTime } from "../../lib/format";
import { useContestDisplayZone } from "../../lib/display-zone";
import { TONE_CLASS, isChecking, uphackTone, useMyUphacks, type OwnUphack } from "../../uphack";

/**
 * "Your hacks" (`EX-HCK01`, the canvas board `UphackSend`): this reader's hacks
 * in this round, newest first, each with what became of it in plain words.
 * Polls every five seconds while one is still with the judge. The setter's
 * answer to an input is never shown here: it is the team's to read.
 */
export function MyUphacksPanel({ slug }: { slug: string }) {
  const t = useTranslations("contest.uphack");
  const tJudge = useTranslations("judge");
  const locale = useLocale();
  const zone = useContestDisplayZone();
  const query = useMyUphacks(slug);

  if (query.isPending) return <Skeleton className="h-48 w-full rounded-xl" />;
  if (query.isError) return <p className="text-sm text-red-700">{t("mineFailed")}</p>;
  const { items, left } = query.data;

  const line = (item: OwnUphack): string => {
    switch (item.status) {
      case "queued":
      case "running":
        return t("msg_checking");
      case "refused":
        return t("msg_refused", { message: item.message ?? "" });
      case "duplicate":
        return t("msg_duplicate", { test: item.duplicateOf ?? 0 });
      case "main_failed":
        return t("msg_main_failed");
      case "not_broken":
        return t("msg_not_broken");
      case "pending":
        return t("msg_pending", { verdict: item.targetVerdict ? tJudge(`verdict.${item.targetVerdict}`) : "" });
      case "approved":
        return t("msg_approved", { test: item.testIdx ?? 0 });
      case "rejected":
        return item.note ? t("msg_rejected_note", { note: item.note }) : t("msg_rejected");
      default:
        return t("msg_errored");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-base font-bold text-neutral-900">{t("mineTitle")}</h3>
        <span className="text-xs tabular-nums text-neutral-500">{t("leftToday", { left })}</span>
      </div>
      {items.length === 0 ? (
        <p className="rounded-xl border border-[rgb(var(--border))] bg-white p-8 text-center text-sm text-[rgb(var(--muted-foreground))]">
          {t("mineEmpty")}
        </p>
      ) : (
        <ul className="overflow-hidden rounded-xl border border-[rgb(var(--border))] bg-white">
          {items.map((item) => {
            const tone = uphackTone(item.status);
            return (
              <li
                key={item.hackId}
                className="grid grid-cols-[2.5rem_minmax(0,1fr)] items-center gap-x-3 gap-y-2 border-b border-[rgb(var(--border))] px-4 py-3 last:border-b-0 sm:grid-cols-[2.5rem_minmax(0,1fr)_auto]"
              >
                <span className="font-mono text-base font-bold text-neutral-900">{item.letter}</span>
                <span className="min-w-0 space-y-0.5">
                  <span className="block truncate text-sm font-medium text-neutral-900">
                    {t("against", { name: item.target?.name ?? t("theirProgram"), id: item.targetSubmissionId.slice(-6) })}
                  </span>
                  <span className={cn("block text-[13px]", tone === "bad" ? "text-red-700" : "text-neutral-600")}>{line(item)}</span>
                  <span className="block text-[11px] tabular-nums text-neutral-400">{formatDateTime(item.createdAt, locale, zone)}</span>
                </span>
                <span
                  className={cn(
                    "col-start-2 inline-flex h-6 w-fit items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold sm:col-start-3",
                    TONE_CLASS[tone],
                  )}
                >
                  {isChecking(item.status) ? <span aria-hidden className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" /> : null}
                  {t(`state_${item.status}`)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <p className="max-w-3xl text-[13px] leading-relaxed text-neutral-500">{t("mineNote")}</p>
    </div>
  );
}
